import React from "react";
import { NavLink, useNavigate, Outlet } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useCompany } from "@/contexts/CompanyContext";
import { useOutlet } from "@/contexts/OutletContext";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  LayoutDashboard, Fuel, Receipt, Truck, Tag, TrendingUp,
  Wallet, Building2, Users, LogOut, ShieldCheck, BookOpen, Scale, Gauge, Landmark, BookText, FileEdit, Building, ClipboardCheck, FileUp, Bell,
} from "lucide-react";

const NAV = [
  { to: "/", label: "Dashboard", icon: LayoutDashboard, testid: "nav-dashboard" },
  { to: "/buku-stock", label: "Buku Stock", icon: Fuel, testid: "nav-buku-stock" },
  { to: "/rekonsiliasi", label: "Rekonsiliasi Tangki", icon: Gauge, testid: "nav-rekonsiliasi" },
  { to: "/penjualan", label: "Penjualan", icon: Receipt, testid: "nav-penjualan" },
  { to: "/pembelian", label: "Pembelian", icon: Truck, testid: "nav-pembelian" },
  { to: "/harga", label: "Harga BBM", icon: Tag, testid: "nav-pengaturan-harga", adminOnly: true },
  { to: "/coa", label: "Chart of Accounts", icon: Landmark, testid: "nav-coa" },
  { to: "/jurnal-umum", label: "Jurnal Umum", icon: BookText, testid: "nav-jurnal-umum" },
  { to: "/jurnal-approval", label: "Approval Jurnal", icon: ClipboardCheck, testid: "nav-jurnal-approval", adminOnly: true },
  { to: "/rekening-koran", label: "Import Rekening Koran", icon: FileUp, testid: "nav-rekening-koran" },
  { to: "/buku-besar", label: "Buku Besar", icon: BookOpen, testid: "nav-buku-besar" },
  { to: "/neraca-awal", label: "Neraca Awal", icon: FileEdit, testid: "nav-neraca-awal" },
  { to: "/laba-rugi", label: "Laba Rugi", icon: TrendingUp, testid: "nav-laporan-laba-rugi" },
  { to: "/neraca", label: "Neraca", icon: Scale, testid: "nav-neraca" },
  { to: "/arus-kas", label: "Arus Kas", icon: Wallet, testid: "nav-laporan-arus-kas" },
  { to: "/aset", label: "Aset & Penyusutan", icon: Building2, testid: "nav-daftar-aset" },
  { to: "/notifications", label: "Notifikasi & Alert", icon: Bell, testid: "nav-notifications" },
  { to: "/outlets", label: "Manajemen Outlet", icon: Building2, testid: "nav-outlets", adminOnly: true },
  { to: "/company", label: "Profil Perusahaan", icon: Building, testid: "nav-company", adminOnly: true },
  { to: "/users", label: "Manajemen User", icon: Users, testid: "nav-user-management", adminOnly: true },
];

const Layout = () => {
  const { user, logout } = useAuth();
  const { company } = useCompany();
  const { outlets, active, switchOutlet } = useOutlet();
  const nav = useNavigate();
  const isAdmin = user?.role === "admin";

  return (
    <div className="min-h-screen flex bg-slate-50">
      {/* Sidebar */}
      <aside className="w-64 shrink-0 bg-[#0B0F17] text-slate-200 flex flex-col">
        <div className="px-6 py-6 border-b border-slate-800/60">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-[#E30613] grid place-items-center shadow-lg shadow-red-900/30 shrink-0">
              <Fuel className="w-5 h-5 text-white" />
            </div>
            <div className="min-w-0">
              <div className="font-extrabold text-white tracking-tight text-base leading-tight truncate">{company?.name || "SPBU ERP"}</div>
              <div className="text-xs text-slate-400 truncate">{company?.tagline || "Financial & Stock"}</div>
            </div>
          </div>
        </div>

        <nav className="flex-1 px-3 py-4 space-y-1">
          {NAV.filter(n => !n.adminOnly || isAdmin).map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              end={n.to === "/"}
              data-testid={n.testid}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-all ${
                  isActive
                    ? "bg-[#E30613] text-white shadow-md shadow-red-950/40"
                    : "text-slate-300 hover:bg-slate-800/70 hover:text-white"
                }`
              }
            >
              <n.icon className="w-4 h-4" />
              <span>{n.label}</span>
            </NavLink>
          ))}
        </nav>

        <div className="p-4 border-t border-slate-800/60">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-9 h-9 rounded-full bg-slate-700 grid place-items-center text-white text-sm font-bold">
              {user?.name?.charAt(0)?.toUpperCase() || "U"}
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-sm font-semibold text-white truncate">{user?.name}</div>
              <div className="flex items-center gap-1">
                {isAdmin && <ShieldCheck className="w-3 h-3 text-emerald-400" />}
                <span className="text-xs text-slate-400 capitalize">{user?.role}</span>
              </div>
            </div>
          </div>
          <Button
            variant="secondary"
            size="sm"
            data-testid="logout-button"
            className="w-full bg-slate-800 hover:bg-slate-700 text-slate-100 border-0"
            onClick={async () => { await logout(); nav("/login"); }}
          >
            <LogOut className="w-4 h-4 mr-2" /> Logout
          </Button>
        </div>
      </aside>

      {/* Main */}
      <main className="flex-1 min-w-0 flex flex-col">
        <header className="h-16 px-6 lg:px-8 flex items-center justify-between bg-white border-b border-slate-200">
          <div>
            <div className="text-xs uppercase tracking-widest text-slate-500 font-semibold">{company?.spbu_code ? `SPBU ${company.spbu_code}` : "SPBU"}</div>
            <div className="text-sm text-slate-700 font-semibold">{company?.name || "PT Pertamina Retail Operator"}</div>
          </div>
          <div className="flex items-center gap-3">
            {outlets.length > 0 && (
              <Select value={active} onValueChange={switchOutlet}>
                <SelectTrigger className="h-9 w-56" data-testid="outlet-selector"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {outlets.map((o) => (
                    <SelectItem key={o.code} value={o.code}>
                      <div className="flex items-center gap-2">
                        <Building2 className="w-3 h-3" />
                        <span>{o.name}</span>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
            <Badge className="bg-emerald-100 text-emerald-800 hover:bg-emerald-100 border-emerald-200">● Online</Badge>
            <div className="text-sm text-slate-600 font-mono-fig hidden lg:block">
              {new Date().toLocaleDateString("id-ID", { weekday: "long", day: "numeric", month: "long", year: "numeric" })}
            </div>
          </div>
        </header>
        <div className="flex-1 p-6 lg:p-8">
          <Outlet />
        </div>
      </main>
    </div>
  );
};

export default Layout;
